<template>
    <div class="body v2-list-page">
        <div class="order-page-container">
            <div class="page-section">
                <a-card :bordered="false" style="width: 100%" :loading="loading">
                    <template slot="title">
                        <a-breadcrumb separator=">">
                            <a-breadcrumb-item>
                                <router-link to="/cliente/monitores/cotacoes">
                                    Home
                                </router-link>
                            </a-breadcrumb-item>
                            <a-breadcrumb-item>
                                <router-link to="/visitas">
                                    Visitas
                                </router-link>
                            </a-breadcrumb-item>
                            <a-breadcrumb-item class="order-page-title">
                                Visita #{{ id }}
                            </a-breadcrumb-item>
                        </a-breadcrumb>
                    </template>
                    <template slot="extra">
                        <div class="title-actions">
                            <a-tag v-if="visit && originOf(visit)" class="status-tag">
                                <i :class="['fa-solid', originOf(visit).icon]"></i>
                                {{ originOf(visit).label }}
                            </a-tag>
                            <a-tag v-if="visit" class="status-tag" :color="statusOf(visit.status).color">{{ statusOf(visit.status).label }}</a-tag>
                            <a-button v-if="editable" type="primary" :loading="closing" @click="checkout">
                                <i v-if="!closing" class="fa-solid fa-flag-checkered"></i>
                                Encerrar visita
                            </a-button>
                        </div>
                    </template>

                    <div v-if="!visit" class="empty-text">Visita não encontrada.</div>
                    <template v-else>
                        <a-row type="flex" :gutter="[24, 16]">
                            <a-col v-for="info in infos" :key="info.label" style="flex: 1 1 0; min-width: 160px">
                                <div class="info-label">{{ info.label }}</div>
                                <div class="info-value">{{ info.value }}</div>
                                <a v-if="info.href" :href="info.href" target="_blank" class="phone-link info-hint">
                                    <i class="fab fa-whatsapp"></i>{{ info.hint }}
                                </a>
                                <div v-else-if="info.hint" class="info-hint">{{ info.hint }}</div>
                            </a-col>
                        </a-row>
                    </template>
                </a-card>
            </div>

            <div v-if="visit" class="page-section">
                <a-card :bordered="false" style="width: 100%" class="table-card">
                    <template slot="title">
                        <div class="v2-tab-hd">
                            <h2>Requisições geradas</h2>
                        </div>
                    </template>
                    <a-table
                        size="small"
                        row-key="id"
                        :columns="columns"
                        :data-source="visit.requisitions"
                        :pagination="false"
                        :scroll="{ x: 'max-content' }"
                        :locale="{ emptyText: 'Nenhuma requisição gerada nesta visita.' }"
                    >
                        <template slot="status" slot-scope="text, row">
                            <a-tag :color="orderStatusOf(row.status).color">{{ orderStatusOf(row.status).label }}</a-tag>
                        </template>
                        <template slot="acoes" slot-scope="text, row">
                            <ActionMenu v-if="requisitionVisible(row)" :actions="orderActions(row)" />
                        </template>
                    </a-table>
                </a-card>
            </div>
        </div>

        <CheckoutModal
            v-if="checkoutDelivery"
            :visit="visit"
            :delivery="checkoutDelivery.delivery"
            :offered="checkoutDelivery.offered"
            @done="fetch"
            @close="checkoutDelivery = null"
        />
    </div>
</template>

<script>
import VisitService from "@/services/v3/visits/visit.service";
import SellerOrderService from "@/services/v3/seller-orders/seller-order.service";
import CheckoutModal from "./modals/checkout.modal.vue";
import ActionMenu from "@/components/popovers/action-menu.popover.vue";
import visitFormat from "./visit-format.mixin";
import { loaderMixin } from "@/mixins/sweet-loader.mixin";
import { VISIT_STATUS, VISIT_ORIGIN, isEditableVisit, isVisibleRequisition } from "./visit-status";
import { ALL_SELLER_ORDER_STATUS } from "@/pages/seller-orders/seller-order-status";
import { has_feature, REQUISITIONS } from "@/helpers/features";

export default {
    name: "VisitPage",
    components: { ActionMenu, CheckoutModal },
    mixins: [visitFormat, loaderMixin],
    props: ["id"],
    data() {
        return {
            service: new VisitService(),
            statusMap: VISIT_STATUS,
            visit: null,
            loading: false,
            closing: false,
            checkoutDelivery: null,
        };
    },
    computed: {
        editable() {
            return isEditableVisit(this.visit);
        },
        infos() {
            const visit = this.visit;
            const seller = visit.seller || {};
            const provider = visit.provider || {};
            return [
                { label: "Vendedor", value: seller.name || "--", hint: seller.phone ? this.formatPhone(seller.phone) : null, href: seller.phone && this.whatsappLink(seller.phone) },
                { label: "Fornecedor", value: provider.name || "--", hint: provider.cnpj },
                { label: "Loja", value: visit.store.name, hint: [visit.store.address, visit.store.city, visit.store.state].filter(Boolean).join(" · ") },
                { label: "Duração", value: this.formatDuration(this.durationBetween(visit.checked_in_at, visit.checked_out_at)) },
                { label: "Check-in", value: this.formatDateTime(visit.checked_in_at), hint: this.distanceHint(visit.checkin_location) },
                { label: "Check-out", value: this.formatDateTime(visit.checked_out_at), hint: this.distanceHint(visit.checkout_location) },
            ];
        },
        columns() {
            return [
                { title: "Requisição", key: "id", customRender: (t, row) => `#${row.id}` },
                { title: "Enviada em", key: "sent_at", customRender: (t, row) => this.formatDateTime(row.sent_at) },
                { title: "Produtos ofertados", key: "offered", align: "center", customRender: (t, row) => this.int(row.totals.offered) },
                { title: "Não ofertados", key: "skipped", align: "center", customRender: (t, row) => this.int(row.totals.skipped + row.totals.pending) },
                { title: "Valor", key: "amount", align: "right", customRender: (t, row) => this.brl(row.totals.amount) },
                { title: "Status", key: "status", scopedSlots: { customRender: "status" } },
                this.canOpenRequisition && { title: "Ação", key: "acoes", align: "center", fixed: "right", width: 70, scopedSlots: { customRender: "acoes" } },
            ].filter(Boolean);
        },
        canOpenRequisition() {
            return has_feature(REQUISITIONS);
        },
    },
    watch: {
        id: "load",
    },
    created() {
        this.load();
    },
    methods: {
        load() {
            this.loading = true;
            this.fetch().finally(() => {
                this.loading = false;
            });
        },
        fetch() {
            return this.service
                .show(this.id)
                .then(({ data }) => {
                    this.visit = data;
                })
                .catch((error) => {
                    this.visit = null;
                    if (!error.response || error.response.status !== 404) this.notifyError(error, "Não foi possível carregar a visita.");
                });
        },
        originOf(visit) {
            return VISIT_ORIGIN[visit.origin] || null;
        },
        checkout() {
            const draft = (this.visit.requisitions || []).find((requisition) => requisition.status === "DRAFT" && requisition.totals.offered > 0);
            if (draft && !this.canOpenRequisition) {
                this.checkoutDelivery = { delivery: null, offered: draft.totals.offered };
                return;
            }
            if (draft) {
                this.closing = true;
                new SellerOrderService()
                    .show(draft.id)
                    .then(({ data }) => {
                        this.checkoutDelivery = { delivery: data.delivery || null, offered: draft.totals.offered };
                    })
                    .catch((error) => this.notifyError(error, "Não foi possível carregar a requisição da visita."))
                    .finally(() => {
                        this.closing = false;
                    });
                return;
            }
            this.confirm_action({
                message: "Encerrar esta visita?",
                subtitle: "Nenhum produto foi ofertado, a requisição fica vazia.",
                confirm_text: "Sim, encerrar",
                cancel_text: "Voltar",
                callback: () => {
                    this.closing = true;
                    this.service
                        .checkout(this.visit.id)
                        .then(() => {
                            this.snack("Visita encerrada.");
                            return this.fetch();
                        })
                        .catch((error) => this.notifyError(error, "Não foi possível encerrar a visita."))
                        .finally(() => {
                            this.closing = false;
                        });
                },
            });
        },
        orderStatusOf(value) {
            return ALL_SELLER_ORDER_STATUS[value] || { label: value || "--", color: "" };
        },
        requisitionVisible(row) {
            return isVisibleRequisition(this.visit, row);
        },
        orderActions(row) {
            const draft = this.editable && row.status === "DRAFT";
            return [
                {
                    label: draft ? "Editar produtos" : "Ver detalhes",
                    icon: draft ? "edit" : "visibility",
                    onClick: () => this.$router.push(`/requisicoes/${row.id}`),
                },
            ];
        },
        distanceHint(location) {
            if (!location || location.distance_m === null || location.distance_m === undefined) return null;
            return `${this.formatDistance(location.distance_m)} da loja`;
        },
    },
};
</script>

<style lang="scss" scoped>
@import "./_detail-page";
</style>
